import { createSelector, createSlice } from '@reduxjs/toolkit'
import { fetchByGeohash } from '@/features/dashboard/map/map-thunks'
import MAP_STYLE from '@/features/dashboard/map/style.json'
import { threeDStyle } from '@/features/dashboard/map/styles/3d'
import { basicStyle } from '@/features/dashboard/map/styles/basic'
import { brightStyle } from '@/features/dashboard/map/styles/bright'
import { darkmatterStyle } from '@/features/dashboard/map/styles/dark-matter'
import { fiordStyle } from '@/features/dashboard/map/styles/fiord'
import { libertyStyle } from '@/features/dashboard/map/styles/liberty'
import { osmStyle } from '@/features/dashboard/map/styles/osm'
import { positronStyle } from '@/features/dashboard/map/styles/positron'
import { terrainStyle } from '@/features/dashboard/map/styles/terrain'
import { tonerStyle } from '@/features/dashboard/map/styles/toner'
import { fetchPlanningAreas, fetchPopupFeatures } from '@/slices/mapThunks'
import * as setUtils from '@/utils/setUtils'

// /////////////////////////////////////////////////////////////////////////////////////////
// ///////////////// Model & Tier Config ////////////////////////////////////////////////////
// /////////////////////////////////////////////////////////////////////////////////////////

const MODEL_TYPES = [
    { name: 'LitterPickGroup', color: '#056a09', radius: 16 },
    { name: 'LitterPickEvent', color: '#a6ef50', radius: 16 },
    { name: 'LitterReport', color: '#F44336', radius: 16 },
    { name: 'Group', color: '#9C27B0', radius: 16 },
    { name: 'Event', color: '#640974', radius: 16 }
]

// Zoom ranges match getFeatureTypeAndPrecisionFromZoom in map-thunks.js
// Layer visibility overlaps by 0.5 to prevent features vanishing during
// small zoom fluctuations caused by panning on a pitched map.
const FEATURE_TYPES = {
    LARGE_COUNT: { minzoom: 4, maxzoom: 7 },
    MEDIUM_COUNT: { minzoom: 6.5, maxzoom: 9 },
    SMALL_COUNT: { minzoom: 8.5, maxzoom: 12 },
    FEATURES: { minzoom: 11.5, maxzoom: 24 }
}

const MODEL_COLOR_DEFAULTS = Object.fromEntries(
    MODEL_TYPES.map((m) => [m.name, m.color])
)

const MODEL_TYPE_NAMES = MODEL_TYPES.map((m) => m.name)
const FEATURE_TYPE_NAMES = Object.keys(FEATURE_TYPES)

function generateModelSources() {
    const sources = {}
    for (const model of MODEL_TYPES) {
        for (const ft of FEATURE_TYPE_NAMES) {
            sources[`${model.name}_${ft}`] = {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] }
            }
        }
    }
    return sources
}

function generateModelLayers() {
    const layers = []
    for (const model of MODEL_TYPES) {
        for (const [ft, zoom] of Object.entries(FEATURE_TYPES)) {
            const sourceId = `${model.name}_${ft}`
            // Circle layer
            layers.push({
                id: sourceId,
                source: sourceId,
                type: 'circle',
                minzoom: zoom.minzoom,
                maxzoom: zoom.maxzoom,
                layout: { visibility: 'visible' },
                paint: {
                    'circle-color': model.color,
                    'circle-radius': [
                        'case',
                        ['has', 'count'],
                        [
                            'interpolate',
                            ['linear'],
                            ['get', 'count'],
                            1,
                            10,
                            10,
                            18,
                            50,
                            26,
                            200,
                            34
                        ],
                        model.radius
                    ],
                    'circle-pitch-scale': 'viewport'
                },
                metadata: {
                    model_type: model.name,
                    feature_type: ft,
                    layer_type: 'map_feature'
                }
            })
            // Count label layer
            layers.push({
                id: `${sourceId}-count`,
                source: sourceId,
                type: 'symbol',
                minzoom: zoom.minzoom,
                maxzoom: zoom.maxzoom,
                filter: ['has', 'count'],
                layout: {
                    'text-field': ['to-string', ['get', 'count']],
                    'text-size': 12,
                    'text-allow-overlap': true
                },
                paint: { 'text-color': '#ffffff' },
                metadata: {
                    model_type: model.name,
                    feature_type: ft,
                    layer_type: 'map_feature'
                }
            })
        }
    }
    return layers
}

// Build the processed style with generated sources and layers
const processedStyle = {
    ...MAP_STYLE,
    sources: { ...MAP_STYLE.sources, ...generateModelSources() },
    layers: [...MAP_STYLE.layers, ...generateModelLayers()]
}

const stylesDict = {
    basicStyle: {
        style: basicStyle,
        spriteUrl: 'https://maps.rosslyoung.com/styles/basic/sprite'
    },
    threeDStyle: {
        style: threeDStyle,
        spriteUrl: ''
    },
    brightStyle: {
        style: brightStyle,
        spriteUrl: 'https://maps.rosslyoung.com/styles/bright/sprite'
    },
    darkmatterStyle: {
        style: darkmatterStyle,
        spriteUrl: 'https://maps.rosslyoung.com/styles/dark/sprite',
        circleColors: {
            LitterPickGroup: '#66BB6A',
            LitterPickEvent: '#AED581',
            LitterReport: '#EF5350',
            Group: '#BA68C8',
            Event: '#F06292'
        },
        // circleStrokeWidth: 2,// sets whether is is visible or not
        circleStrokeColors: {
            LitterPickGroup: '#ffffff',
            LitterPickEvent: '#ffffff',
            LitterReport: '#ffffff',
            Group: '#ffffff',
            Event: '#ffffff'
        }
    },
    fiordStyle: {
        style: fiordStyle,
        spriteUrl: 'https://maps.rosslyoung.com/styles/fiord/sprite'
    },
    libertyStyle: {
        style: libertyStyle,
        spriteUrl: 'https://maps.rosslyoung.com/styles/liberty/sprite'
    },
    osmStyle: {
        style: osmStyle,
        spriteUrl: 'https://maps.rosslyoung.com/styles/osm/sprite',
        circleStrokeWidth: 2, // sets whether is is visible or not
        circleStrokeColors: {
            LitterPickGroup: '#000000',
            LitterPickEvent: '#000000',
            LitterReport: '#000000',
            Group: '#000000',
            Event: '#000000'
        }
    },
    positronStyle: {
        style: positronStyle,
        spriteUrl: 'https://maps.rosslyoung.com/styles/positron/sprite'
    },
    tonerStyle: {
        style: tonerStyle,
        spriteUrl: 'https://maps.rosslyoung.com/styles/toner/sprite'
    }
}

// /////////////////////////////////////////////////////////////////////////////////////////
// ///////////////// Calculate Map Initial Stata & Reducers ////////////////////////////////
// /////////////////////////////////////////////////////////////////////////////////////////

const interactiveLayerIds = []

const defaultViewport = {
    latitude: 55.7679533,
    longitude: -4.1908921,
    zoom: 5,
    bearing: 0,
    pitch: 0,
    width: window.innerWidth,
    height: window.innerHeight,
    interactiveLayerIds
}

const initialState = {
    viewport: defaultViewport,
    mapStyle: processedStyle,

    layerVisibility: {},

    showBaseMap: true,
    selectedMapStyle: 'osmStyle',
    selectedTheme: 'spen',
    selectedLineStyle: 'layerStyleDefaultView',

    fetchingByGeohashStatus: 'idle',

    // Normalized geohash tracking: keys are `${modelType}:${featureType}`
    // Example: 'LitterPickGroup:LARGE_COUNT': { fetching: ['gcpuv'], fetched: ['gcpuw'] }
    geohashTracker: {},

    startupDataFinished: false
}

const getLayerIndex = (layers, idToFind) =>
    layers.findIndex((layer) => layer.id === idToFind)

const slice = createSlice({
    name: 'map',
    initialState,
    reducers: {
        // startMapReducer(state, action) {
        //     state.mapStyle.sources.openmaptiles.tiles = [
        //         action.payload.maptile_url
        //     ]
        //     state.mapStyle.glyphs = action.payload.glyphs_url
        // },
        onChangeViewport(state, action) {
            Object.assign(state.viewport, action.payload)
        },
        geohashesFetching(state, action) {
            const { modelType, featureType, geohashes } = action.payload
            const key = `${modelType}:${featureType}`

            if (!state.geohashTracker[key]) {
                state.geohashTracker[key] = { fetching: [], fetched: [] }
            }

            state.geohashTracker[key].fetching = Array.from(
                setUtils.union(
                    new Set(state.geohashTracker[key].fetching),
                    new Set(geohashes)
                )
            )
        },
        geohashesFetched(state, action) {
            const { modelType, featureType, geohashes } = action.payload
            const key = `${modelType}:${featureType}`

            if (!state.geohashTracker[key]) {
                state.geohashTracker[key] = { fetching: [], fetched: [] }
            }

            const tracker = state.geohashTracker[key]
            tracker.fetching = Array.from(
                setUtils.difference(
                    new Set(tracker.fetching),
                    new Set(geohashes)
                )
            )
            tracker.fetched = Array.from(
                setUtils.union(new Set(tracker.fetched), new Set(geohashes))
            )
        },
        deleteAllSourceFeatures(state, action) {
            for (const modelType of MODEL_TYPE_NAMES) {
                for (const ft of FEATURE_TYPE_NAMES) {
                    const key = `${modelType}_${ft}`
                    if (state.mapStyle.sources[key]) {
                        state.mapStyle.sources[key].data.features.length = 0
                    }
                }
            }

            // Clear all geohash tracking
            state.geohashTracker = {}
        },
        clearGeohashesForModelType(state, action) {
            const { modelType } = action.payload
            for (const ft of FEATURE_TYPE_NAMES) {
                const sourceKey = `${modelType}_${ft}`
                if (state.mapStyle.sources[sourceKey]) {
                    state.mapStyle.sources[sourceKey].data.features.length = 0
                }
            }
            // Clear all tracking entries for this model type
            for (const key of Object.keys(state.geohashTracker)) {
                if (key.startsWith(`${modelType}:`)) {
                    delete state.geohashTracker[key]
                }
            }
        },
        changeMapStyle(state, action) {
            const styleConfig = stylesDict[action.payload.styleName]
            state.selectedMapStyle = action.payload.styleName

            const featureLayers = state.mapStyle.layers.filter(
                (layer) => layer.metadata.layer_type !== 'base_map'
            )

            // Apply per-style circle paint overrides (or reset to defaults)
            for (const layer of featureLayers) {
                if (layer.type === 'circle' && layer.metadata.model_type) {
                    const modelType = layer.metadata.model_type
                    layer.paint['circle-color'] =
                        styleConfig.circleColors?.[modelType] ??
                        MODEL_COLOR_DEFAULTS[modelType]
                    layer.paint['circle-stroke-width'] =
                        styleConfig.circleStrokeWidth ?? 0
                    layer.paint['circle-stroke-color'] =
                        styleConfig.circleStrokeColors?.[modelType] ??
                        'transparent'
                }
            }

            state.mapStyle.layers = [...styleConfig.style, ...featureLayers]
            state.mapStyle.sprite = styleConfig.spriteUrl
        },

        toggleLayerVisibility(state, action) {
            const { visibility } =
                state.mapStyle.layers[
                    getLayerIndex(state.mapStyle.layers, action.payload)
                ].layout
            const newVisibility = visibility === 'visible' ? 'none' : 'visible'
            state.mapStyle.layers[
                getLayerIndex(state.mapStyle.layers, action.payload)
            ].layout.visibility = newVisibility
        },
        toggleAllBaseLayers(state, action) {
            state.showBaseMap = action.payload
            state.mapStyle.layers = state.mapStyle.layers.map((layer) => {
                if (layer.metadata.layer_type === 'base_map') {
                    layer.layout.visibility =
                        action.payload === true ? 'visible' : 'none'
                }
                return layer
            })
        },
        // this action is also called in tabs reducer - sets style in style.json and records it in tab component....
        // to decide what toggle to show in layers tab and what line legend to show in legends tab
        setBaseLineLayerStyle(state, action) {
            console.log(
                'action.payload.styleExpression',
                action.payload.styleExpression
            )
            state.mapStyle.layers = state.mapStyle.layers.map((layer) => {
                if (layer.metadata.feature_type === 'base_lines') {
                    layer.paint['line-color'] = action.payload.styleExpression
                }
                return layer
            })
            state.selectedTheme = action.payload.newTheme
            state.selectedLineStyle = action.payload.newLineStyle
        }
        // fetchLitterPickByGeohash(state, action) {
        //     console.log('fetchLitterPickByGeohash')
        //     if(action.payload.featureType === 'LARGE_COUNT') {
        //         state.mapStyle.sources.large_litter_event_count.data.features.push(...action.payload.response.events)
        //         state.mapStyle.sources.large_litter_group_count.data.features.push(...action.payload.response.groups)
        //     }else if (action.payload.featureType === 'SMALL_COUNT') {
        //         state.mapStyle.sources.small_litter_event_count.data.features.push(...action.payload.response.events)
        //         state.mapStyle.sources.small_litter_group_count.data.features.push(...action.payload.response.groups)
        //     }else if (action.payload.featureType === 'FEATURES') {
        //         state.mapStyle.sources.litter_event_features.data.features.push(...action.payload.response.events)
        //         state.mapStyle.sources.litter_group_features.data.features.push(...action.payload.response.groups)
        //     }
        // },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchByGeohash.pending, (state, action) => {
            state.fetchingByGeohashStatus = 'pending'
        })
        builder.addCase(fetchByGeohash.fulfilled, (state, action) => {
            const { featureType, modelTypes, geohashes, response } =
                action.payload

            // Write features to tier-specific sources (e.g. LitterPickGroup_LARGE_COUNT)
            for (const [modelType, features] of Object.entries(response.data)) {
                const sourceKey = `${modelType}_${featureType}`
                if (
                    state.mapStyle.sources[sourceKey] &&
                    Array.isArray(features)
                ) {
                    state.mapStyle.sources[sourceKey].data.features.push(
                        ...features
                    )
                }
            }

            // Mark geohashes as fetched
            for (const modelType of modelTypes) {
                const key = `${modelType}:${featureType}`
                if (!state.geohashTracker[key]) {
                    state.geohashTracker[key] = { fetching: [], fetched: [] }
                }
                const tracker = state.geohashTracker[key]
                tracker.fetching = Array.from(
                    setUtils.difference(
                        new Set(tracker.fetching),
                        new Set(geohashes)
                    )
                )
                tracker.fetched = Array.from(
                    setUtils.union(new Set(tracker.fetched), new Set(geohashes))
                )
            }

            state.fetchingByGeohashStatus = 'idle'
        })
        builder.addCase(fetchByGeohash.rejected, (state, action) => {
            state.fetchingByGeohashStatus = 'idle'
        })
    }

    //
    //     'map/fetchByGeohash/pending'  : (state, action)=>{state.fetchingByGeohashStatus='pending'},
    //     'map/fetchByGeohash/fulfilled': (state, action)=>{
    //         if(action.payload.featureType === 'LARGE_COUNT') {
    //             state.mapStyle.sources.large_litter_event_count.data.features.push(...action.payload.response.events)
    //             state.mapStyle.sources.large_litter_group_count.data.features.push(...action.payload.response.groups)
    //         }else if (action.payload.featureType === 'SMALL_COUNT') {
    //             state.mapStyle.sources.small_litter_event_count.data.features.push(...action.payload.response.events)
    //             state.mapStyle.sources.small_litter_group_count.data.features.push(...action.payload.response.groups)
    //         }else if (action.payload.featureType === 'FEATURES') {
    //             state.mapStyle.sources.litter_event_features.data.features.push(...action.payload.response.events)
    //             state.mapStyle.sources.litter_group_features.data.features.push(...action.payload.response.groups)
    //         }
    //         state.fetchingByGeohashStatus='idle'
    //     },
    //     'map/fetchByGeohash/rejected': (state, action)=>{state.fetchingByGeohashStatus='idle'}
    // }
})

export const {
    startMapReducer,
    changeMapStyle,
    onChangeViewport,
    fetchLitterPickByGeohash,
    setVoltageLevelsToFetch,
    deleteAllSourceFeatures,
    clearGeohashesForModelType,
    fetchingNodesLinesBuildingsSuccess,
    geohashesFetching,
    geohashesFetched,
    deletePlanningAreasFeatures,
    addCustomLayersAndSources,
    hideAllCustomLayersExcept,
    setBuildingsVisibility,
    toggleLayerVisibility,
    toggleAllBaseLayers,
    setBaseLineLayerStyle,
    setBuildingOverlay,
    setBaseOverlays,
    toggleDataOverlayVoltageVisibility,
    filterBaseLayersByCircuitOrSSIDorFeeder,
    addFeatureToSelectedLayer,
    addTraceDataToMap,
    clearSimpleTraceDataFromMap,
    selectSimpleOrScenarioTrace,
    setTraceOverlayVisibility,
    deletePopupFeatures,
    setSearchStatus,
    setBoundaryVisibility,
    toggleBoundaryLabelVisibility,
    toggleBoundaryLayerVisibility
} = slice.actions

// export default slice.reducer
export default slice.reducer

// ///////////////////////////////////////////////////////////////////////////////////
// /////////////////////////////// Selectors /////////////////////////////////////////
// ///////////////////////////////////////////////////////////////////////////////////

const getLVNodes = (state) =>
    state.map.mapStyle.sources.base_lv_node_detailed_source.data.features

const getHVNodes = (state) =>
    state.map.mapStyle.sources.base_hv_node_detailed_source.data.features

const getEHVNodes = (state) =>
    state.map.mapStyle.sources.base_ehv_node_detailed_source.data.features

const getLVLines = (state) =>
    state.map.mapStyle.sources.base_lv_line_detailed_source.data.features

const getHVLines = (state) =>
    state.map.mapStyle.sources.base_hv_line_detailed_source.data.features

const getEHVLines = (state) =>
    state.map.mapStyle.sources.base_ehv_line_detailed_source.data.features

const getBuildings = (state) =>
    state.map.mapStyle.sources['3d_building_source'].data.features

export const selectLVNodeCount = createSelector([getLVNodes], (nodes) =>
    // console.log("selector - map - selectNodeCount")
    nodes ? nodes.length : 0
)
export const selectHVNodeCount = createSelector([getHVNodes], (nodes) =>
    // console.log("selector - map - selectNodeCount")
    nodes ? nodes.length : 0
)
export const selectEHVNodeCount = createSelector([getEHVNodes], (nodes) =>
    // console.log("selector - map - selectNodeCount")
    nodes ? nodes.length : 0
)

export const selectLVLineCount = createSelector([getLVLines], (lines) =>
    // console.log("selector - map - selectLVLineCount")
    lines ? lines.length : 0
)
export const selectHVLineCount = createSelector([getHVLines], (lines) =>
    // console.log("selector - map - selectHVLineCount")
    lines ? lines.length : 0
)
export const selectEHVLineCount = createSelector([getEHVLines], (lines) =>
    // console.log("selector - map - selectEHVLineCount")
    lines ? lines.length : 0
)
export const selectBuildingsCount = createSelector(
    [getBuildings],
    (buildings) =>
        // console.log("selector - map - selectEHVLineCount")
        buildings ? buildings.length : 0
)

const sort_by = function (field, reverse, primer) {
    const key = primer
        ? function (x) {
              return primer(x[field])
          }
        : function (x) {
              return x[field]
          }
    reverse = !reverse ? 1 : -1

    return function (a, b) {
        return (a = key(a)), (b = key(b)), reverse * ((a > b) - (b > a))
    }
}

export function selectOldChartDistanceData(state) {
    if (state.tab.selectedCircuit) {
        const circuitNodes = state.map.nodes.features.filter(
            (feature) => feature.properties.circuit == state.tab.selectedCircuit
        )
        const sortedCircuits = circuitNodes.sort(sort_by('id', false, parseInt))

        const chartData = sortedCircuits.map((feature) => {
            const featureObj = { name: feature.properties.id }
            const nodeObj = { length: feature.properties.dist_to_source }
            return { ...featureObj, ...nodeObj }
        })
        // console.log(chartData)
        return chartData
    }
    return []
}

/// /////////////////////////////// Cable Data /////////////////////////////////

const getCables = (state) => state.map.cableData
const getProxyCables = (state) => state.map.proxyCableData

export const selectCableData = createSelector([getCables], (cables) => {
    if (cables) {
        return cables.map((cable) => ({
            key: cable.id,
            text: cable.name,
            value: cable.id
        }))
    }
    return []
})

export const selectProxyCableData = createSelector(
    [getProxyCables],
    (cables) => {
        if (cables) {
            return cables.map((cable) => ({
                key: cable.id,
                text: cable.name,
                value: cable.id
            }))
        }
        return []
    }
)

// Selector to get fetched geohashes for a specific model type and feature type
export const selectGeohashTracker = (state, modelType, featureType) => {
    const key = `${modelType}:${featureType}`
    return state.map.geohashTracker[key] || { fetching: [], fetched: [] }
}
